import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { hasPermission, isAssignable } from '../utils/roles';
import { fmtDateTimeBoth, fmtMinutes } from '../utils/dates';
import RequestAttachments from '../components/RequestAttachments';
import { CLOSE_REASON_AR, REQ_CATEGORY_AR, REQ_STATUS_AR, REQ_STATUS_BADGE, fillCanned } from '../utils/requests';
import { PRIORITY_AR } from '../utils/labels';

const emptyConvert = { assigned_to: '', project_id: '', workflow_id: '', priority: 'medium', due_date: '', title: '', reply: '' };

const TABS = [
  { key: 'new', label: 'جديدة' },
  { key: 'awaiting_client', label: 'بانتظار العميل' },
  { key: 'assigned', label: 'جارٍ العمل' },
  { key: 'completed', label: 'مُنجزة' },
  { key: 'closed', label: 'مغلقة' },
  { key: '', label: 'الكل' },
];

const isOpen = (r) => !['closed', 'rejected'].includes(r.status);
const draftKey = (id) => `request-draft-${id}`;

function since(iso) {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `منذ ${mins}د`;
  if (mins < 60 * 24) return `منذ ${fmtMinutes(mins)}`;
  return `منذ ${Math.round(mins / 1440)} يوم`;
}

function Chip({ children, tone = 'muted' }) {
  const tones = {
    muted: { background: 'var(--gray-100)', color: 'var(--text-muted)' },
    danger: { background: '#fee2e2', color: '#b91c1c' },
    warn: { background: '#fef3c7', color: '#92400e' },
    info: { background: 'var(--primary-light)', color: 'var(--primary)' },
  };
  return (
    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap', ...tones[tone] }}>
      {children}
    </span>
  );
}

/**
 * بطاقة التذكرة في طابور الدعم — تعرض ما يلزم للفرز دون فتح التفاصيل.
 */
function QueueCard({ request: r, active, onOpen }) {
  return (
    <div
      className="card"
      onClick={() => onOpen(r)}
      style={{
        cursor: 'pointer', padding: 12,
        borderRight: active ? '3px solid var(--primary)' : '3px solid transparent',
        background: active ? 'var(--surface-alt)' : undefined,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          <span style={{ color: '#9ca3af', fontSize: 12 }}>#{r.id}</span> {r.title}
        </div>
        <span className={`badge badge-${REQ_STATUS_BADGE[r.status] || 'pending'}`}>{REQ_STATUS_AR[r.status] || r.status}</span>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
        👤 {r.client?.name || '—'} · {since(r.created_at)}
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        {r.source === 'whatsapp' && <Chip tone="info">📱 واتساب</Chip>}
        {r.source === 'email' && <Chip tone="info">✉️ بريد</Chip>}
        {r.overdue && <Chip tone="danger">⏰ تجاوزت مهلة أول رد</Chip>}
        {r.unread_messages > 0 && <Chip tone="info">💬 {r.unread_messages} جديدة</Chip>}
        {r.category && <Chip>{REQ_CATEGORY_AR[r.category] || r.category}</Chip>}
        {['high', 'urgent'].includes(r.priority) && <Chip tone="warn">⚡ {PRIORITY_AR[r.priority]}</Chip>}
        {r.handler?.name && <Chip>🙋 {r.handler.name}</Chip>}
      </div>
    </div>
  );
}

/**
 * خيط موحّد: الرسائل والملاحظات الداخلية ومحطات دورة الحياة في تسلسل واحد.
 */
function Timeline({ request, messages, height = 340 }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [messages.length]);

  const events = [
    { at: request.created_at, kind: 'event', text: 'وصلت التذكرة' },
    ...messages.map(m => ({ at: m.created_at, kind: 'message', message: m })),
    request.first_response_at && { at: request.first_response_at, kind: 'event', text: 'أول ردّ على العميل' },
    request.escalated_at && { at: request.escalated_at, kind: 'event', text: '⏰ صُعِّدت لتجاوز مهلة الرد' },
    request.completed_at && { at: request.completed_at, kind: 'event', text: 'أُنجزت وبانتظار تأكيد العميل' },
    request.closed_at && { at: request.closed_at, kind: 'event', text: `أُغلقت ${CLOSE_REASON_AR[request.close_reason] || ''}` },
    request.rated_at && { at: request.rated_at, kind: 'event', text: `قيّم العميل الخدمة ${request.rating}/5` },
  ].filter(Boolean).sort((a, b) => new Date(a.at) - new Date(b.at));

  return (
    <div style={{
      maxHeight: height, overflowY: 'auto', display: 'grid', gap: 10,
      padding: 8, background: 'var(--surface-alt)', borderRadius: 8,
    }}>
      {events.map((e, i) => e.kind === 'event' ? (
        <div key={`e${i}`} style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
          — {e.text} · {fmtDateTimeBoth(e.at)} —
        </div>
      ) : (
        <div key={`m${e.message.id}`} style={{ display: 'flex', justifyContent: e.message.from_client ? 'flex-end' : 'flex-start' }}>
          <div style={{
            maxWidth: '82%', padding: '9px 12px', borderRadius: 12,
            background: e.message.is_internal ? '#fef3c7' : e.message.from_client ? 'var(--surface)' : 'var(--primary-light)',
            border: `1px solid ${e.message.is_internal ? '#fcd34d' : 'var(--border)'}`,
          }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
              {e.message.is_internal && <strong style={{ color: '#92400e' }}>ملاحظة داخلية · </strong>}
              {e.message.sender?.name || (e.message.from_client ? 'العميل' : 'رد آلي')} · {fmtDateTimeBoth(e.message.created_at)}
              {!e.message.from_client && !e.message.is_internal && e.message.read_at && <span> · قُرئت</span>}
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{e.message.body}</div>
            {e.message.attachments?.length > 0 && (
              <div style={{ marginTop: 8 }}><RequestAttachments items={e.message.attachments} /></div>
            )}
          </div>
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
}

export default function Requests() {
  const { api, apiUpload, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const canManage = hasPermission(user, 'requests.manage');
  const fileRef = useRef(null);

  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [desk, setDesk] = useState({ canned: [], categories: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('new');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('newest');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [converting, setConverting] = useState(null);
  const [form, setForm] = useState(emptyConvert);

  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replyClose, setReplyClose] = useState(false);
  const [internal, setInternal] = useState(false);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [assisting, setAssisting] = useState('');
  const [summary, setSummary] = useState('');

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (category) params.set('category', category);
    if (sort !== 'newest') params.set('sort', sort);
    if (overdueOnly) params.set('overdue', '1');
    if (query) params.set('q', query);

    setLoading(true);
    api(`/client-requests?${params}`).then(setRequests).catch(() => {}).finally(() => setLoading(false));
    api('/client-requests/stats').then(setStats).catch(() => {});
  }, [api, statusFilter, category, sort, overdueOnly, query]);

  useEffect(load, [load]);

  useEffect(() => {
    api('/client-requests/desk').then(setDesk).catch(() => {});
    if (canManage) {
      api('/users').then(u => setUsers((u || []).filter(isAssignable))).catch(() => {});
      api('/projects').then(setProjects).catch(() => {});
      api('/workflows').then(setWorkflows).catch(() => {});
    }
  }, []);

  // البحث بعد توقّف الكتابة حتى لا نستدعي الـ API على كل حرف
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const loadThread = useCallback((requestId) => {
    setLoadingThread(true);
    api(`/client-requests/${requestId}/messages`)
      .then(d => setMessages(d.messages || []))
      .catch(() => setMessages([]))
      .finally(() => setLoadingThread(false));
  }, [api]);

  // التذكرة المفتوحة تأتي من المسار، فيمكن مشاركة رابطها مع بقية الفريق
  useEffect(() => {
    if (!id) { setSelected(null); setMessages([]); return; }
    api(`/client-requests/${id}`).then(r => {
      setSelected(r);
      setReplyBody(localStorage.getItem(draftKey(r.id)) || '');
      setReplyClose(false);
      setInternal(false);
      setFiles([]);
      setSummary('');
    }).catch(() => setSelected(null));
    loadThread(id);
  }, [id]);

  useEffect(() => {
    if (!selected) return;
    if (replyBody.trim()) localStorage.setItem(draftKey(selected.id), replyBody);
    else localStorage.removeItem(draftKey(selected.id));
  }, [replyBody, selected]);

  const openTicket = (r) => navigate(`/requests/${r.id}`);
  const closeTicket = () => navigate('/requests');

  const refreshSelected = () => {
    if (!selected) return;
    api(`/client-requests/${selected.id}`).then(setSelected).catch(() => {});
    loadThread(selected.id);
    load();
  };

  const openConvert = (r, reply = '') => {
    setConverting(r);
    setForm({ ...emptyConvert, project_id: r.project_id || '', title: r.title, priority: r.priority || 'medium', reply });
  };

  const doConvert = async () => {
    if (!form.assigned_to) { toast.warning('اختر المختص المسؤول'); return; }
    try {
      await api(`/client-requests/${converting.id}/convert`, {
        method: 'POST',
        body: JSON.stringify({
          assigned_to: Number(form.assigned_to),
          project_id: form.project_id || null,
          workflow_id: form.workflow_id || null,
          priority: form.priority,
          due_date: form.due_date || null,
          title: form.title || null,
          reply: form.reply.trim() || null,
        }),
      });
      toast.success(form.reply.trim() ? 'تم إرسال الرد وتحويل الطلب إلى مهمة' : 'تم تحويل الطلب إلى مهمة');
      if (form.reply.trim()) localStorage.removeItem(draftKey(converting.id));
      setConverting(null);
      setReplyBody('');
      refreshSelected();
    } catch (err) { toast.error(err.message); }
  };

  const uploadReplyFiles = async (requestId, messageId) => {
    for (const file of files) {
      const fd = new FormData();
      fd.append('files[]', file);
      fd.append('message_id', messageId);
      try {
        await apiUpload(`/client-requests/${requestId}/attachments`, fd);
      } catch (err) { toast.error(`${file.name}: ${err.message}`); }
    }
  };

  const doReply = async () => {
    if (!replyBody.trim()) { toast.warning('اكتب الرد أولاً'); return; }
    setSending(true);
    try {
      const res = await api(`/client-requests/${selected.id}/reply`, {
        method: 'POST',
        body: JSON.stringify({ body: replyBody.trim(), close: replyClose, internal }),
      });
      if (files.length) await uploadReplyFiles(selected.id, res.reply.id);

      toast.success(res.message);
      localStorage.removeItem(draftKey(selected.id));
      setReplyBody('');
      setFiles([]);
      setReplyClose(false);
      if (fileRef.current) fileRef.current.value = '';
      refreshSelected();
    } catch (err) { toast.error(err.message); }
    finally { setSending(false); }
  };

  const claim = async () => {
    try {
      const res = await api(`/client-requests/${selected.id}/claim`, { method: 'POST', body: JSON.stringify({}) });
      toast.success(res.message);
      refreshSelected();
    } catch (err) {
      if (!window.confirm(`${err.message}. هل تستلمها بدلاً عنه؟`)) return;
      try {
        await api(`/client-requests/${selected.id}/claim`, { method: 'POST', body: JSON.stringify({ force: true }) });
        toast.success('استلمت التذكرة');
        refreshSelected();
      } catch (e) { toast.error(e.message); }
    }
  };

  const retag = async (patch) => {
    try {
      const updated = await api(`/client-requests/${selected.id}`, { method: 'PUT', body: JSON.stringify(patch) });
      setSelected(updated);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const assist = async (action) => {
    setAssisting(action);
    try {
      const res = await api(`/client-requests/${selected.id}/assist`, {
        method: 'POST',
        body: JSON.stringify({ action, draft: replyBody.trim() || null }),
      });
      if (action === 'improve') setReplyBody(res.text);
      else if (action === 'summarize') setSummary(res.text);
      else {
        const s = res.suggestion || {};
        if (!s.category && !s.priority) { toast.warning('لم يصل اقتراح واضح'); return; }
        const label = [s.category && REQ_CATEGORY_AR[s.category], s.priority && PRIORITY_AR[s.priority]]
          .filter(Boolean).join(' · ');
        if (window.confirm(`اقتراح: ${label}\n${s.reason || ''}\n\nهل تعتمده؟`)) {
          await retag({ category: s.category || null, priority: s.priority || null });
          toast.success('حُدّث التصنيف');
        }
      }
    } catch (err) { toast.error(err.message); }
    finally { setAssisting(''); }
  };

  const reject = async () => {
    const reason = window.prompt('سبب الاعتذار (اختياري):', '');
    if (reason === null) return;
    try {
      await api(`/client-requests/${selected.id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) });
      toast.success('تم رفض الطلب');
      refreshSelected();
    } catch (err) { toast.error(err.message); }
  };

  const closeNow = async () => {
    if (!window.confirm(`إغلاق الطلب "${selected.title}" دون انتظار ردّ العميل؟`)) return;
    try {
      await api(`/client-requests/${selected.id}/close`, { method: 'POST', body: JSON.stringify({}) });
      toast.success('تم إغلاق الطلب');
      refreshSelected();
    } catch (err) { toast.error(err.message); }
  };

  const insertCanned = (index) => {
    const item = desk.canned?.[index];
    if (!item) return;
    const text = fillCanned(item.body, {
      client: selected?.client?.name, id: selected?.id, title: selected?.title, user: user?.name,
    });
    setReplyBody(b => (b.trim() ? `${b.trim()}\n\n${text}` : text));
  };

  const availableWorkflows = workflows.filter(w => !w.project_id || String(w.project_id) === String(form.project_id));
  const canReply = canManage && selected && (isOpen(selected) || selected.reopenable || internal);
  const tabCount = (key) => {
    if (!stats) return null;
    if (key === '') return Object.values(stats.by_status || {}).reduce((a, b) => a + b, 0);
    return stats.by_status?.[key] || 0;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>طلبات العملاء</h2>
        {stats && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
            <Chip tone={stats.overdue > 0 ? 'danger' : 'muted'}>متأخرة: {stats.overdue}</Chip>
            <Chip tone={stats.unread > 0 ? 'info' : 'muted'}>ردود غير مقروءة: {stats.unread}</Chip>
            <Chip>متوسط أول رد: {stats.metrics?.avg_first_response_minutes != null ? fmtMinutes(stats.metrics.avg_first_response_minutes) : '—'}</Chip>
            <Chip>متوسط الإغلاق: {stats.metrics?.avg_resolution_hours != null ? `${stats.metrics.avg_resolution_hours}س` : '—'}</Chip>
            <Chip>متوسط التقييم: {stats.metrics?.avg_rating != null ? `${stats.metrics.avg_rating}/5` : '—'}</Chip>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {TABS.map(t => (
          <button
            key={t.key || 'all'}
            className={`btn btn-sm ${statusFilter === t.key ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setStatusFilter(t.key)}
          >
            {t.label}{tabCount(t.key) != null ? ` (${tabCount(t.key)})` : ''}
          </button>
        ))}
      </div>

      <div className="filters" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input
          className="form-input" style={{ minWidth: 200 }} placeholder="بحث بالعنوان أو الوصف أو العميل…"
          value={search} onChange={e => setSearch(e.target.value)}
        />
        <select className="form-select" value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">كل التصنيفات</option>
          {Object.entries(desk.categories || REQ_CATEGORY_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="form-select" value={sort} onChange={e => setSort(e.target.value)}>
          <option value="newest">الأحدث أولاً</option>
          <option value="oldest">الأقدم أولاً</option>
          <option value="priority">حسب الأولوية</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)} />
          المتأخرة فقط
        </label>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 12 }}>
        <div style={{ flex: '1 1 320px', minWidth: 300, display: 'grid', gap: 8, maxHeight: '72vh', overflowY: 'auto' }}>
          {loading ? <div className="loader">جاري التحميل...</div> : requests.length === 0 ? (
            <div className="card"><div className="empty"><div className="empty-icon">📨</div>لا توجد طلبات</div></div>
          ) : requests.map(r => (
            <QueueCard key={r.id} request={r} active={String(r.id) === String(id)} onOpen={openTicket} />
          ))}
        </div>

        {selected && (
          <div className="card" style={{ flex: '2 1 460px', minWidth: 320 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div>
                <h3 style={{ margin: 0 }}>
                  <span style={{ color: '#9ca3af', fontSize: 14 }}>#{selected.id}</span> {selected.title}
                </h3>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  👤 {selected.client?.name || '—'}
                  {selected.project?.title && <span> • 📁 {selected.project.title}</span>}
                  <span> • {fmtDateTimeBoth(selected.created_at)}</span>
                </div>
              </div>
              <button className="btn-icon" onClick={closeTicket} title="إغلاق">✕</button>
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              <span className={`badge badge-${REQ_STATUS_BADGE[selected.status] || 'pending'}`}>
                {REQ_STATUS_AR[selected.status] || selected.status}
              </span>
              {selected.overdue && <Chip tone="danger">⏰ تجاوزت مهلة أول رد</Chip>}
              {!selected.first_response_at && selected.first_response_due_at && !selected.overdue && (
                <Chip tone="warn">مهلة الرد حتى {fmtDateTimeBoth(selected.first_response_due_at)}</Chip>
              )}
              {selected.handler?.name ? <Chip>🙋 يعالجها {selected.handler.name}</Chip> : <Chip tone="warn">لم يستلمها أحد</Chip>}
              {selected.reopened_count > 0 && <Chip tone="warn">↺ أُعيد فتحها {selected.reopened_count} مرة</Chip>}
              {selected.reopenable && <Chip>نافذة إعادة الفتح مفتوحة</Chip>}
            </div>

            {canManage && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <select className="form-select" style={{ maxWidth: 190 }} value={selected.category || ''}
                  onChange={e => retag({ category: e.target.value || null })}>
                  <option value="">بلا تصنيف</option>
                  {Object.entries(desk.categories || REQ_CATEGORY_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select className="form-select" style={{ maxWidth: 150 }} value={selected.priority || 'medium'}
                  onChange={e => retag({ priority: e.target.value })}>
                  {Object.entries(PRIORITY_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            )}

            {selected.description && (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {selected.description}
              </div>
            )}

            {selected.attachments?.filter(a => !a.client_message_id).length > 0 && (
              <div style={{ marginTop: 10 }}>
                <RequestAttachments items={selected.attachments.filter(a => !a.client_message_id)} />
              </div>
            )}

            {selected.task && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                🔗 المهمة: {selected.task.title} ({selected.task.status})
              </div>
            )}

            {selected.status === 'awaiting_client' && (
              <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 8 }}>
                ⏳ بانتظار ردّ العميل على استفسارنا
              </div>
            )}

            {selected.status === 'completed' && selected.auto_close_at && (
              <div style={{ fontSize: 12, color: 'var(--warning)', marginTop: 8 }}>
                ⏳ بانتظار ردّ العميل — إغلاق تلقائي في {fmtDateTimeBoth(selected.auto_close_at)}
              </div>
            )}

            {selected.client_feedback && (
              <div style={{ fontSize: 13, marginTop: 8, padding: 8, background: 'var(--gray-100)', borderRadius: 6 }}>
                <strong>ردّ العميل:</strong> {selected.client_feedback}
              </div>
            )}

            {selected.status === 'closed' && selected.closer?.name && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                ✅ أغلقها {selected.closer.name}
              </div>
            )}

            {selected.status === 'rejected' && selected.admin_notes && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                سبب الاعتذار: {selected.admin_notes}
              </div>
            )}

            {selected.rating > 0 && (
              <div style={{ fontSize: 13, marginTop: 8 }}>
                <span style={{ color: '#f59e0b', letterSpacing: 1 }}>{'★'.repeat(selected.rating)}{'☆'.repeat(5 - selected.rating)}</span>
                <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>تقييم العميل {selected.rating}/5</span>
                {selected.rating_comment && <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>{selected.rating_comment}</div>}
              </div>
            )}

            {summary && (
              <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: 'var(--gray-100)', fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                <strong>ملخّص الخيط:</strong>
                <div style={{ marginTop: 6 }}>{summary}</div>
              </div>
            )}

            <div style={{ marginTop: 14 }}>
              {loadingThread ? <div className="loader">جاري التحميل...</div> : <Timeline request={selected} messages={messages} />}
            </div>

            {canReply ? (
              <div style={{ marginTop: 12 }}>
                {desk.canned?.length > 0 && (
                  <select className="form-select" style={{ marginBottom: 8 }} value=""
                    onChange={e => { insertCanned(Number(e.target.value)); e.target.value = ''; }}>
                    <option value="">إدراج ردّ جاهز…</option>
                    {desk.canned.map((c, i) => <option key={i} value={i}>{c.title}</option>)}
                  </select>
                )}

                <textarea
                  className="form-textarea" style={{ minHeight: 96, background: internal ? '#fffbeb' : undefined }}
                  placeholder={internal ? 'ملاحظة داخلية بين الفريق — لا تصل العميل' : 'الرد على العميل…'}
                  value={replyBody} onChange={e => setReplyBody(e.target.value)}
                />

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
                  <button className="btn btn-sm btn-outline" disabled={!!assisting} onClick={() => assist('summarize')}>
                    {assisting === 'summarize' ? '...' : '✨ لخّص الخيط'}
                  </button>
                  <button className="btn btn-sm btn-outline" disabled={!!assisting || !replyBody.trim()} onClick={() => assist('improve')}>
                    {assisting === 'improve' ? '...' : '✨ حسّن الصياغة'}
                  </button>
                  <button className="btn btn-sm btn-outline" disabled={!!assisting} onClick={() => assist('triage')}>
                    {assisting === 'triage' ? '...' : '✨ اقترح تصنيفاً'}
                  </button>
                  <input ref={fileRef} type="file" multiple style={{ fontSize: 12, flex: 1 }}
                    onChange={e => setFiles(Array.from(e.target.files || []))} />
                </div>

                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8, fontSize: 13 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={internal} onChange={e => { setInternal(e.target.checked); if (e.target.checked) setReplyClose(false); }} />
                    ملاحظة داخلية
                  </label>
                  {!internal && isOpen(selected) && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={replyClose} onChange={e => setReplyClose(e.target.checked)} />
                      إغلاق الطلب بعد الرد
                    </label>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  <button className="btn btn-primary" onClick={doReply} disabled={sending || !replyBody.trim()}>
                    {sending ? 'جارٍ الإرسال...' : internal ? 'حفظ الملاحظة' : replyClose ? 'رد وإغلاق' : !isOpen(selected) ? 'رد وإعادة فتح' : 'إرسال الرد'}
                  </button>
                  {!selected.handler || selected.handler.id !== user?.id ? (
                    <button className="btn btn-outline" onClick={claim} disabled={!isOpen(selected)}>استلام التذكرة</button>
                  ) : null}
                  {['new', 'awaiting_client'].includes(selected.status) && !selected.task_id && (
                    <button className="btn btn-outline" onClick={() => openConvert(selected, replyBody)}>رد وتحويل لمهمة…</button>
                  )}
                  {['new', 'awaiting_client'].includes(selected.status) && (
                    <button className="btn btn-outline" style={{ color: 'var(--danger)' }} onClick={reject}>اعتذار</button>
                  )}
                  {['assigned', 'completed', 'awaiting_client'].includes(selected.status) && (
                    <button className="btn btn-outline" onClick={closeNow}>إغلاق الطلب</button>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
                {canManage ? 'الطلب مغلق وانتهت نافذة إعادة الفتح — العرض للاطلاع فقط' : 'لا تملك صلاحية الرد على العملاء'}
              </div>
            )}
          </div>
        )}
      </div>

      {converting && (
        <div className="modal-overlay" onClick={() => setConverting(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-title">تحويل الطلب إلى مهمة</div>
            <div className="form-group">
              <label className="form-label">عنوان المهمة</label>
              <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">المشروع</label>
                <select className="form-select" value={form.project_id} onChange={e => {
                  const pid = e.target.value;
                  setForm(f => {
                    const wf = workflows.find(w => String(w.id) === String(f.workflow_id));
                    const keep = wf && (!wf.project_id || String(wf.project_id) === String(pid));
                    return { ...f, project_id: pid, workflow_id: keep ? f.workflow_id : '' };
                  });
                }}>
                  <option value="">— بدون —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">المسار (اختياري)</label>
                <select className="form-select" value={form.workflow_id} onChange={e => setForm(f => ({ ...f, workflow_id: e.target.value }))}>
                  <option value="">بدون مسار</option>
                  {availableWorkflows.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">المختص المسؤول *</label>
                <select className="form-select" value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}>
                  <option value="">— اختر —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">الأولوية</label>
                <select className="form-select" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  {Object.entries(PRIORITY_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">تاريخ الاستحقاق</label>
              <input className="form-input" type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">رد للعميل مع التحويل (اختياري)</label>
              <textarea className="form-textarea" style={{ minHeight: 70 }}
                placeholder="يصل العميل رداً مكتوباً بدل إشعار الاستلام العام"
                value={form.reply} onChange={e => setForm(f => ({ ...f, reply: e.target.value }))} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={doConvert} disabled={!form.assigned_to}>
                {form.reply.trim() ? 'رد وتحويل' : 'تحويل'}
              </button>
              <button className="btn btn-outline" onClick={() => setConverting(null)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
